import React, { useState, useEffect } from 'react';

// ============================================================================
// SELF-CONTAINED EASTER EGG PAGE - "The Vault Vision"
// Copy this entire file to your app and import it as a route
// Requires: Tailwind CSS configured in your app
// ============================================================================

// Embedded content data
const visionData = {
  title: "The Last Library of Human Kind",
  subtitle: "We're Building the System That Organizes All Knowledge",
  hero: {
    tagline: "Not just a better search engine. A navigable map of everything humanity knows. Where proteins are ordered by reactivity, laws by precedent, art by meaning, and emergency protocols by urgency.",
    status: "Live in Production • Serving Critical Infrastructure"
  },
  audiences: {
    enterprise: {
      name: "Enterprise",
      icon: "🏢",
      hero: {
        title: "Deploy Your Private Knowledge Vault",
        subtitle: "Enterprise-grade semantic organization for mission-critical knowledge"
      },
      sections: [
        {
          heading: "The Problem Is Exponential",
          content: "Humanity has produced more data in the last two years than in all of history combined. But we can't find anything. Knowledge workers spend 2.5 hours per day searching. Researchers rediscover what was already known. Emergency responders can't access critical protocols fast enough. Every book ever written, every paper published, every procedure documented—buried in an exponentially growing sea of noise. As AI-generated content floods the internet, authentic human knowledge becomes harder to distinguish, harder to find, and at risk of being lost forever.",
          metric: "Human knowledge drowning in generated content"
        },
        {
          heading: "We're Not Building Better Search",
          content: "We're building something that has never existed: a semantic organization layer for all human knowledge. Not folders. Not keywords. Not similarity matching. True understanding. Imagine a library where proteins are shelved by reactivity, laws by their precedent relationships, procedures by urgency and context, art by emotion and meaning. Where every piece of knowledge is mapped by what it means, what it does, and how it relates to everything else. This is The Vault.",
          metric: "Organizing knowledge by meaning, not metadata"
        },
        {
          heading: "Millions of Librarians, Zero New Content",
          content: "When AI made intelligence abundant, everyone asked: how do we replace workers? We asked a different question: what impossible project can we finally tackle? Our answer: employ a million AI librarians to read, organize, and map every artifact of human knowledge. They don't generate. They don't create. They preserve, organize, and illuminate. They turn an unsearchable chaos into a navigable cosmos.",
          metric: "AI as librarians, not content generators"
        },
        {
          heading: "Already Operational",
          content: "This isn't a pitch deck fantasy. Right now, firefighters in the Netherlands access 10,000+ life-saving procedures in under 3 seconds during active emergencies. The Dutch Ministry of Justice navigates 30+ years of legal documents to answer citizen requests in hours instead of weeks. Research organizations organize entire protein libraries by functional properties. The Vault is live. The librarians are working. And we're just getting started.",
          metric: "3 deployments • 100K+ queries • 3 sec avg response"
        },
        {
          heading: "The Insights Layer: See What You've Never Seen",
          content: "When we build your Vector Ontology—a semantic map of everything your organization knows—we don't just organize. We illuminate. Hidden correlations between departments that never talk. Gaps in your knowledge where critical procedures should exist but don't. Causal chains that explain why certain processes fail. Contradictions between policies written years apart. Your data becomes a mirror that shows you what you didn't know you didn't know. A digital twin of your organization's knowledge.",
          metric: "Gaps • Correlations • Causations • Contradictions"
        },
        {
          heading: "Your Universal Vector Ontology",
          content: "Every organization has implicit knowledge structures—but they're invisible. We make them explicit. Your Vector Ontology maps every concept, every relationship, every dependency in your knowledge base. See how a regulation change ripples through a hundred procedures. Identify the single-point-of-failure experts whose knowledge isn't documented. Discover which teams are solving the same problems independently. This isn't just search. It's organizational self-awareness.",
          metric: "Your knowledge, finally visible"
        },
        {
          heading: "Your Organization's Knowledge",
          content: "We start by proving the system works at your scale. Your private Vault. Your proprietary knowledge. Emergency protocols, legal archives, research databases, technical documentation—organized semantically and accessed through Rhea, your AI librarian. Your data never trains models or touches public systems. This is how we fund the mission: one organization at a time, one knowledge domain at a time, building toward the complete library.",
          metric: "€10K - €500K per deployment"
        },
        {
          heading: "The Vision: All Knowledge, Perfectly Organized",
          content: "Every book. Every paper. Every patent. Every procedure. Every documented discovery in human history—organized not by upload date or keyword, but by semantic meaning and relationship. A library so comprehensive and so elegantly organized that finding anything becomes effortless. Where research becomes discovery. Where learning becomes flow. This is the last library humanity will ever need to build. And we're building it now, before the noise makes it impossible.",
          metric: "The infrastructure for human knowledge, forever"
        }
      ],
      cta: {
        primary: "Schedule a Demo",
        secondary: "View Case Studies"
      }
    },
    contributors: {
      name: "Contributors",
      icon: "🌍",
      hero: {
        title: "Help Build the Last Library",
        subtitle: "Contribute data, run librarian nodes, preserve human knowledge"
      },
      sections: [
        {
          heading: "The Mission Needs You",
          content: "We're building the infrastructure to preserve all human knowledge. But we can't do it alone. We need data contributors who believe in preserving authentic human artifacts. We need node operators who will run the librarian AI workers. We need the open source community to help us build something bigger than any single company. This is humanity's library. It should be built by humanity.",
          metric: "Open source • Community driven • Forever"
        },
        {
          heading: "Contribute Your Data",
          content: "Have a dataset that represents real human knowledge? Research papers, historical documents, technical procedures, cultural artifacts? Contribute it to The Vault. We organize it semantically and make it accessible to everyone. Your data remains yours—you control licensing and access. But by contributing to the organized layer, you help build the commons. Small datasets are free. Large institutional datasets receive support and compensation.",
          metric: "From personal archives to institutional collections"
        },
        {
          heading: "Run a Librarian Node",
          content: "The Vault needs millions of AI librarians to read, organize, and map knowledge. You can run librarian nodes on your hardware. These nodes don't generate content—they analyze, categorize, and build semantic relationships. It's like SETI@home or Folding@home, but for organizing human knowledge. Node operators earn credits that grant priority access to The Vault and share in the network's success.",
          metric: "Distributed compute • Earn rewards • Own the network"
        },
        {
          heading: "Open Source Librarians",
          content: "Our core librarian models and organization algorithms are open source. Not because we don't value them, but because preserving human knowledge is too important to be proprietary. Anyone can audit how we organize knowledge. Anyone can improve the algorithms. Anyone can fork and build their own specialized vault. The code is public. The mission is collective.",
          metric: "Apache 2.0 • Fully transparent • Community governed"
        },
        {
          heading: "The Network Effect",
          content: "Every dataset contributed teaches the system new domains. Every node operator adds processing power. Every algorithm improvement benefits everyone. As the network grows, the library becomes more comprehensive, more organized, more valuable. Early contributors have the most impact and receive permanent recognition as library founders. You're not just contributing to a product. You're building infrastructure that will outlast all of us.",
          metric: "Network effects • Permanent legacy • Compound growth"
        },
        {
          heading: "Building the Universal Vector Ontology",
          content: "Every dataset you contribute doesn't just add content—it teaches the system new ways of organizing meaning. When you contribute medical protocols, you help define how urgency relates to procedure. When you contribute legal documents, you map how precedent flows through time. Together, we're building a Universal Vector Ontology: a semantic coordinate system for all human knowledge. Your data helps define the dimensions. Your domain expertise shapes how concepts relate across fields.",
          metric: "Your data shapes the structure of knowledge itself"
        },
        {
          heading: "Join the Builder Community",
          content: "We have a global community of contributors, node operators, and developers. Weekly calls. Open roadmap. Transparent governance. Whether you contribute data, run nodes, write code, or spread the word—you're part of building the last library. This is bigger than The Vault. This is about ensuring human knowledge survives and remains accessible. Forever.",
          metric: "Discord • GitHub • Global community"
        }
      ],
      cta: {
        primary: "Join the Community",
        secondary: "Run a Node"
      }
    },
    researchers: {
      name: "Researchers",
      icon: "🔬",
      hero: {
        title: "Research Tools That Think Like You",
        subtitle: "Semantic navigation across all of human knowledge"
      },
      sections: [
        {
          heading: "Research Is Broken",
          content: "You spend more time searching than thinking. Reading abstracts trying to find the one paper that matters. Rediscovering what someone published in 1987. Missing connections between your field and adjacent domains. The knowledge exists. You just can't find it. Not because it's not indexed, but because it's not organized by meaning.",
          metric: "60% of research time spent searching"
        },
        {
          heading: "Semantic Research Navigation",
          content: "Ask Rhea: 'Show me all experiments using CRISPR on neurons that had unexpected results.' She doesn't keyword match. She understands. She maps every paper by methodology, domain, results, and implications. She finds the paper from neuroscience, the one from genetics, and the overlooked study from 2003 that's exactly what you need. Organized by relevance to your actual question.",
          metric: "Cross-domain discovery • True semantic search"
        },
        {
          heading: "Everything, Connected",
          content: "The Vault doesn't just store papers—it maps the relationship between ideas. Every citation. Every methodology. Every contradictory result. Every replication. You see not just what was published, but how it fits into the larger structure of knowledge. Find patterns no one else has noticed because no one else can see the full map.",
          metric: "Billions of semantic relationships"
        },
        {
          heading: "The Insights Engine: Discover What's Missing",
          content: "The Vector Ontology doesn't just show what exists—it reveals what's absent. Gaps in the literature where obvious experiments were never run. Correlations between fields that have never been connected. Causal hypotheses implied by patterns across thousands of papers. See which methodologies consistently produce contradictory results. Find the overlooked study from an adjacent field that challenges your assumptions. Research becomes not just finding—but discovering.",
          metric: "Gaps • Correlations • Hidden causations • Overlooked connections"
        },
        {
          heading: "Your Field's Vector Ontology",
          content: "Every research domain has a hidden structure—implicit relationships, assumed hierarchies, unwritten rules. We make them visible. See how your field evolved. Identify the foundational papers that actually shaped methodology vs. the highly-cited ones that didn't. Map the genealogy of ideas. Find where your research fits in the larger structure—and where it could bridge to unexplored territory.",
          metric: "See your field like never before"
        },
        {
          heading: "Free for Researchers",
          content: "Academic researchers get full access. No paywalls. No subscription fees. Read anything. Navigate everything. Contribute your papers and help build the commons. The Vault is funded by enterprise deployments, which means researchers get the tool for free. This is how research should work: all knowledge, instantly accessible, forever.",
          metric: "100% free for academic research"
        },
        {
          heading: "Your Research, Discoverable",
          content: "When you publish to The Vault, your work isn't buried in journal issue 47 of volume 23. It's semantically mapped and discoverable by anyone asking the right questions—even if they don't know your paper exists. Your work finds the researchers who need it most. Citations increase not because of impact factor, but because of actual relevance.",
          metric: "Semantic discoverability • Real impact"
        },
        {
          heading: "The Research Commons",
          content: "We're building the tool researchers have always needed but could never build individually. A complete, organized, navigable map of human discovery. Where finding what matters is faster than reading what doesn't. Where connections reveal themselves. Where research becomes discovery again. Join us.",
          metric: "Research at the speed of thought"
        }
      ],
      cta: {
        primary: "Request Research Access",
        secondary: "Publish Your Work"
      }
    },
    public: {
      name: "Everyone",
      icon: "✨",
      hero: {
        title: "A Library That Actually Works",
        subtitle: "All human knowledge, organized by meaning, accessible to all"
      },
      sections: [
        {
          heading: "Imagine This Library",
          content: "You walk into a library. Infinite corridors stretch in every direction. But you're not alone. Rhea, your personal librarian, knows exactly what you're looking for. 'Show me everything about sustainable architecture in hot climates.' She guides you through examples, research, case studies—not in chronological order, but organized by relevance, feasibility, and impact. Learning becomes exploration. Questions become journeys.",
          metric: "Knowledge as adventure"
        },
        {
          heading: "Not Search. Navigation.",
          content: "You don't need to know the right keywords. You don't need to scroll through pages of results. You just ask. Like talking to someone who has read everything and remembers perfectly. 'Why do we dream?' She shows you neuroscience, psychology, philosophy, indigenous wisdom—organized so each builds on the last. Understanding flows naturally.",
          metric: "Ask questions • Get understanding"
        },
        {
          heading: "Everything Humanity Knows",
          content: "Every book ever written. Every scientific paper. Every documented procedure. Every recipe. Every historical account. Every piece of art and its context. Not scattered across the internet, buried in databases, locked behind paywalls. All organized. All connected. All accessible. This is what a library should be.",
          metric: "Billions of artifacts • One coherent system"
        },
        {
          heading: "See the Shape of Knowledge",
          content: "The Universal Vector Ontology lets you see what humanity knows—and what it doesn't. Explore the gaps: questions no one has answered, connections no one has made, contradictions no one has resolved. See how ideas from ancient philosophy connect to modern neuroscience. Discover correlations between art movements and political upheavals. Watch causation chains ripple across centuries. Knowledge isn't just facts—it's structure. And now you can see it.",
          metric: "Gaps • Patterns • Hidden connections"
        },
        {
          heading: "Free to Explore",
          content: "Knowledge should be free. The Vault is funded by organizations deploying private instances, which means public access stays free. Forever. Anyone, anywhere can explore the full library. Learn anything. Discover everything. No ads. No tracking. No paywalls. Just knowledge, organized, accessible.",
          metric: "Free forever • No ads • No tracking"
        },
        {
          heading: "Why Now Matters",
          content: "The internet is flooding with AI-generated content. Soon, it will be impossible to tell what was created by humans and what was synthesized. The window to preserve authentic human knowledge is closing. We're building The Vault now, before the noise makes the signal irretrievable. This is our generation's library of Alexandria. Except this one won't burn.",
          metric: "Preserve the signal • Before it's too late"
        },
        {
          heading: "You Can Help",
          content: "Use it. Share it. Contribute to it. Tell others. This library belongs to humanity. The more people who use it, contribute to it, and believe in it, the stronger it becomes. Whether you're a student, a professional, or just someone who loves learning—this is your library. Help us build it.",
          metric: "Your library • Our mission • Forever"
        }
      ],
      cta: {
        primary: "Explore The Vault",
        secondary: "Learn More"
      }
    }
  },
  metadata: {
    created: "2025-11-20",
    version: "1.0",
    project: "Thoughtful Oasis - The Vault"
  }
} as const;

type AudienceKey = keyof typeof visionData.audiences;

interface Section {
  heading: string;
  content: string;
  metric?: string;
}

// Embedded styles (add to your global CSS or use styled-jsx)
const EasterEggStyles = `
  .vault-easter-egg {
    --vault-amber: #f59e0b;
    --vault-amber-light: #fbbf24;
    --vault-slate-900: #0f172a;
    --vault-slate-800: #1e293b;
    --vault-slate-700: #334155;
    --vault-slate-400: #94a3b8;
    --vault-slate-300: #cbd5e1;
    --vault-slate-200: #e2e8f0;
    --vault-slate-100: #f1f5f9;
  }
  
  .vault-easter-egg .vault-fade-in {
    animation: vaultFadeIn 1s ease-in;
  }
  
  @keyframes vaultFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .vault-easter-egg .vault-glow {
    text-shadow: 0 0 20px rgba(251, 191, 36, 0.3);
  }
  
  .vault-easter-egg .vault-text-balance {
    text-wrap: balance;
  }
`;

export default function Vision(): React.ReactElement {
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState<string[]>([]);
  const [activeAudience, setActiveAudience] = useState<AudienceKey>('enterprise');

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => 
                Array.from(new Set([...prev, entry.target.id]))
            );
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.vault-section').forEach(section => {
      observer.observe(section);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [activeAudience]);

  const parallaxStyle = {
    transform: `translateY(${scrollY * 0.5}px)`,
  };

  const currentContent = visionData.audiences[activeAudience];
  const audiences = (Object.keys(visionData.audiences) as AudienceKey[]).map(key => ({
    key,
    ...visionData.audiences[key]
  }));

  return (
    <>
      <style>{EasterEggStyles}</style>
      <div className="vault-easter-egg min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-slate-100 font-serif">
        {/* Audience Selector Tabs */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex flex-wrap justify-center gap-2">
              {audiences.map(audience => (
                <button
                  key={audience.key}
                  onClick={() => {
                    setActiveAudience(audience.key);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                    activeAudience === audience.key
                      ? 'bg-amber-500 text-slate-900 shadow-lg'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  <span className="text-xl">{audience.icon}</span>
                  <span>{audience.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto" style={parallaxStyle}>
            <div className="mb-8 vault-fade-in">
              <svg className="w-24 h-24 mx-auto mb-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            
            <div className="mb-6 vault-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-full text-green-400 text-sm font-semibold">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {visionData.hero.status}
              </span>
            </div>

            <h1 className="text-6xl md:text-8xl font-bold mb-6 vault-glow text-amber-400 vault-fade-in tracking-tight" style={{ animationDelay: '0.1s' }}>
              The Vault
            </h1>
            
            <h2 className="text-4xl md:text-6xl font-light mb-8 text-slate-200 vault-fade-in vault-text-balance leading-tight" style={{ animationDelay: '0.2s' }}>
              {currentContent.hero.title}
            </h2>
            
            <p className="text-xl md:text-2xl text-amber-400 max-w-3xl mx-auto mb-6 vault-fade-in vault-text-balance font-semibold" style={{ animationDelay: '0.3s' }}>
              {currentContent.hero.subtitle}
            </p>

            <p className="text-base md:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed vault-fade-in vault-text-balance" style={{ animationDelay: '0.4s' }}>
              {visionData.hero.tagline}
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center vault-fade-in" style={{ animationDelay: '0.5s' }}>
              <button 
                onClick={() => document.getElementById('vault-contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg"
              >
                {currentContent.cta.primary}
              </button>
              <button 
                onClick={() => document.getElementById('vault-vision')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-transparent border-2 border-amber-500 hover:bg-amber-500/10 text-amber-400 font-semibold rounded-lg transition-all transform hover:scale-105"
              >
                {currentContent.cta.secondary}
              </button>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>

        {/* Stats Bar - Show for Enterprise and Researchers */}
        {(activeAudience === 'enterprise' || activeAudience === 'researchers') && (
          <div className="bg-slate-800/60 backdrop-blur-sm border-y border-slate-700/50 py-12">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">3+</div>
                  <div className="text-slate-400 text-sm md:text-base">Active Deployments</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">100K+</div>
                  <div className="text-slate-400 text-sm md:text-base">Successful Queries</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">3 sec</div>
                  <div className="text-slate-400 text-sm md:text-base">Avg Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">10-100x</div>
                  <div className="text-slate-400 text-sm md:text-base">Faster Information Access</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div id="vault-vision" className="max-w-5xl mx-auto px-6 py-20 space-y-16">
          {currentContent.sections.map((section: Section, index: number) => (
            <div
              key={index}
              id={`vault-section-${index}`}
              className={`vault-section transition-all duration-1000 ${
                visibleSections.includes(`vault-section-${index}`)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-slate-700/50 hover:border-amber-500/50 transition-all duration-500 shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center text-slate-900 font-bold text-lg shadow-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 text-amber-400">
                      {section.heading}
                    </h3>
                    <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-4 vault-text-balance">
                      {section.content}
                    </p>
                    {section.metric && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm font-semibold">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        {section.metric}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vision Scale Section - Show for Enterprise and Public */}
        {(activeAudience === 'enterprise' || activeAudience === 'public') && (
          <div className="bg-slate-800/30 backdrop-blur-sm border-y border-slate-700/50 py-20">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-16">
                <h3 className="text-3xl md:text-4xl font-bold text-amber-400 mb-6">The Scale of the Mission</h3>
                <p className="text-xl text-slate-300 max-w-3xl mx-auto vault-text-balance">
                  {activeAudience === 'enterprise' ? 
                    "We're not optimizing search. We're reorganizing all human knowledge. Starting with organizations that need it most." :
                    "We're building something that has never existed: a complete, organized, navigable map of all human knowledge."}
                </p>
              </div>
            
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="text-center">
                  <div className="text-5xl font-bold text-amber-400 mb-3">∞</div>
                  <div className="text-lg font-semibold text-slate-200 mb-2">All Knowledge</div>
                  <div className="text-sm text-slate-400">Every book, paper, patent, procedure, and documented discovery</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-amber-400 mb-3">1M+</div>
                  <div className="text-lg font-semibold text-slate-200 mb-2">AI Librarians</div>
                  <div className="text-sm text-slate-400">Reading, organizing, mapping relationships—never generating</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-amber-400 mb-3">⚡</div>
                  <div className="text-lg font-semibold text-slate-200 mb-2">Instant Access</div>
                  <div className="text-sm text-slate-400">From chaos to flow state. Research becomes discovery.</div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:border-amber-500/50 transition-all">
                  <div className="text-3xl mb-3">🚒</div>
                  <h4 className="font-bold text-slate-200 mb-2 text-sm">Emergency Services</h4>
                  <p className="text-slate-400 text-xs">10K+ protocols • 3 sec access</p>
                </div>
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:border-amber-500/50 transition-all">
                  <div className="text-3xl mb-3">⚖️</div>
                  <h4 className="font-bold text-slate-200 mb-2 text-sm">Government & Legal</h4>
                  <p className="text-slate-400 text-xs">30+ years • Hours not weeks</p>
                </div>
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:border-amber-500/50 transition-all">
                  <div className="text-3xl mb-3">🔬</div>
                  <h4 className="font-bold text-slate-200 mb-2 text-sm">Research & Development</h4>
                  <p className="text-slate-400 text-xs">Semantic protein organization</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div id="vault-contact" className="max-w-4xl mx-auto px-6 py-20">
          <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 backdrop-blur-sm rounded-2xl p-12 border border-blue-700/50 shadow-2xl text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 mx-auto text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-4xl font-bold mb-6 text-amber-400">
              {activeAudience === 'enterprise' ? 'Join the Mission' : 
               activeAudience === 'contributors' ? 'Start Contributing' :
               activeAudience === 'researchers' ? 'Get Research Access' :
               'Explore The Library'}
            </h3>
            <p className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8 vault-text-balance">
              {activeAudience === 'enterprise' ? 
                "We're building the last library humanity will ever need. One deployment at a time, one knowledge domain at a time. Your organization's vault funds the infrastructure that will preserve and organize all human knowledge. Forever." :
               activeAudience === 'contributors' ?
                "Run a librarian node, contribute datasets, or join our developer community. Every contribution helps build the infrastructure that will preserve human knowledge forever. Get started today." :
               activeAudience === 'researchers' ?
                "Academic researchers get full access to The Vault. All knowledge, semantically organized, instantly accessible. Apply for research access or publish your work to help build the commons." :
                "Start exploring the library today. Ask questions, discover connections, learn anything. Free forever. This is your library. Welcome home."}
            </p>
            {activeAudience === 'enterprise' && (
              <div className="bg-slate-900/50 rounded-lg p-6 mb-8 max-w-xl mx-auto">
                <div className="text-sm text-slate-400 mb-2">Investment Range</div>
                <div className="text-2xl font-bold text-amber-400">€10K - €500K</div>
                <div className="text-sm text-slate-400 mt-2">Based on data volume and deployment complexity</div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {activeAudience === 'enterprise' ? (
                <>
                  <a href="mailto:contact@thevault.ai" className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg inline-block">
                    Schedule Technical Demo
                  </a>
                  <a href="#vault-vision" className="px-8 py-4 bg-transparent border-2 border-amber-500 hover:bg-amber-500/10 text-amber-400 font-semibold rounded-lg transition-all inline-block">
                    View Case Studies
                  </a>
                </>
              ) : activeAudience === 'contributors' ? (
                <>
                  <a href="https://github.com/thevault" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg inline-block">
                    Join GitHub
                  </a>
                  <a href="https://discord.gg/thevault" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-transparent border-2 border-amber-500 hover:bg-amber-500/10 text-amber-400 font-semibold rounded-lg transition-all inline-block">
                    Join Discord
                  </a>
                </>
              ) : activeAudience === 'researchers' ? (
                <>
                  <a href="mailto:research@thevault.ai" className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg inline-block">
                    Request Access
                  </a>
                  <a href="#vault-vision" className="px-8 py-4 bg-transparent border-2 border-amber-500 hover:bg-amber-500/10 text-amber-400 font-semibold rounded-lg transition-all inline-block">
                    Submit Research
                  </a>
                </>
              ) : (
                <>
                  <a href="/explore" className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg inline-block">
                    Start Exploring
                  </a>
                  <a href="#vault-vision" className="px-8 py-4 bg-transparent border-2 border-amber-500 hover:bg-amber-500/10 text-amber-400 font-semibold rounded-lg transition-all inline-block">
                    Learn How It Works
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-slate-800 mt-20">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-amber-400 mb-2">The Vault</h4>
              <p className="text-slate-400">
                Preserving human knowledge. Enabling human discovery.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8 text-center md:text-left">
              <div>
                <h5 className="font-semibold text-slate-300 mb-3">Solutions</h5>
                <ul className="space-y-2 text-slate-400">
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Emergency Services</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Government & Legal</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Research & Academia</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Enterprise Knowledge</a></li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold text-slate-300 mb-3">Company</h5>
                <ul className="space-y-2 text-slate-400">
                  <li><a href="#" className="hover:text-amber-400 transition-colors">About Us</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Case Studies</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Careers</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Contact</a></li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold text-slate-300 mb-3">Resources</h5>
                <ul className="space-y-2 text-slate-400">
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Documentation</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">API Reference</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Contribute Data</a></li>
                  <li><a href="#" className="hover:text-amber-400 transition-colors">Privacy & Security</a></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-slate-800 pt-8 text-center">
              <p className="text-slate-500 text-sm">
                © 2025 {visionData.metadata.project}. Building the last library of human kind.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

